<template>
  <PortalLayout section="Resource Links">
    <div v-if="isLoading"><Loading /></div>
    <div v-else>
      <form v-on:submit.prevent class="my-3">
        <input
          v-model.lazy="searchText"
          placeholder="Search"
          class="w-full md:w-3/4 lg:w-1/2 shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
        <SearchButton
          @click="searchLinks"
          class="block ml-0 mt-2 md:mt-0 md:inline md:ml-2"
          type="submit"
        >
          Search
        </SearchButton>
      </form>
      <span class="w-full flex my-2" v-if="linkOrderChanged && !isLoading">
        <p class="grow text-red-700">
          Your sort order changes are unsaved, when done sorting, please hit the 'Save
          Changes' button to save
        </p>
        <SaveButton @click="saveSortedLinks" />
      </span>
      <AddButton class="mt-2 mb-4" @click="importLinks">
        Import Links from Portal
      </AddButton>
      <div v-if="linkSections?.length > 0" class="mt-2 columns-1 lg:columns-2">
        <AddButton @click="addSection(true)">Add Link Section</AddButton>
        <div
          class="my-2 p-2 break-inside-avoid-column flex bg-gray-100"
          v-for="linkSection in linkSections"
          :key="linkSection.id"
        >
          <div class="flex-col mr-2">
            <i-heroicons-outline-chevron-up
              class="h-5 w-5 mt-1 mb-2"
              :class="
                linkSection.linkSectionSortOrder ==
                Math.min(...linkSections.map((ls) => ls.linkSectionSortOrder))
                  ? 'text-gray-400'
                  : 'cursor-pointer hover:bg-blue-200'
              "
              @click="swapLinkSectionSort(linkSection.linkSectionSortOrder, true)"
            />
            <i-heroicons-outline-chevron-down
              class="h-5 w-5"
              :class="
                linkSection.linkSectionSortOrder ==
                Math.max(...linkSections.map((ls) => ls.linkSectionSortOrder))
                  ? 'text-gray-400'
                  : 'cursor-pointer hover:bg-blue-200'
              "
              @click="swapLinkSectionSort(linkSection.linkSectionSortOrder, false)"
            />
          </div>
          <div class="flex-grow">
            <div class="mb-3">
              <h2 class="text-md font-bold inline">{{ linkSection.subheading }}</h2>
              <div class="inline float-right">
                <EditButton @click="editSection(linkSection)" iconOnly="true" />
                <AddButton @click="addLink(linkSection)" />
              </div>
            </div>
            <div
              v-for="link in linkSection.links"
              :key="link.linkId"
              class="flex cursor-pointer"
            >
              <div
                v-if="!link.isKnownGood"
                :title="
                  link.isWarningSuppressed
                    ? 'Detected broken link, warning suppressed'
                    : 'Detected broken link'
                "
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 m-1 inline float-left align-middle"
                  :class="link.isWarningSuppressed ? 'text-slate-400' : 'text-orange-400'"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
              </div>

              <div
                v-if="link.isKnownGood && link.isWarningSuppressed"
                title="Link is not broken, however warnings are suppressed. It is not recommended to suppress verified links."
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 m-1 inline float-left align-middle text-slate-400"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
              </div>

              <span
                class="text-sm underline hover:bg-blue-200 grow"
                @click="editLink(link, linkSection)"
              >
                {{ link.label }}
              </span>

              <i-heroicons-outline-chevron-up
                class="h-4 w-4 m-1 inline float-right align-middle"
                :class="
                  link.linkSortOrder ==
                  Math.min(...linkSection.links.map((l) => l.linkSortOrder))
                    ? 'text-gray-400'
                    : 'cursor-pointer hover:bg-blue-200'
                "
                @click="swapLinkSort(linkSection, link.linkSortOrder, true)"
              />
              <i-heroicons-outline-chevron-down
                class="h-4 w-4 m-1 inline float-right align-middle"
                :class="
                  link.linkSortOrder ==
                  Math.max(...linkSection.links.map((l) => l.linkSortOrder))
                    ? 'text-gray-400'
                    : 'cursor-pointer hover:bg-blue-200'
                "
                @click="swapLinkSort(linkSection, link.linkSortOrder, false)"
              />
            </div>
          </div>
        </div>
        <AddButton @click="addSection(false)">Add Link Section</AddButton>
      </div>
      <div v-else>
        <div class="my-2">-- No Links found for this building --</div>
        <AddButton @click="addSection(false)">Add Link Section</AddButton>
      </div>
    </div>

    <EditPopup
      entity-name="Link"
      :is-visible="isLinkPopupVisible"
      :is-edit-mode="isEditing"
      :save-error="saveError"
      :delete-enabled="true"
      @close="isLinkPopupVisible = false"
      @save="saveLink"
      @delete="deleteLink"
    >
      <div>
        <div
          v-if="!isKnownGood"
          :title="
            isWarningSuppressed
              ? 'Detected broken link, warning suppressed'
              : 'Detected broken link'
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5 m-1 inline float-left align-middle"
            :class="isWarningSuppressed ? 'text-slate-400' : 'text-orange-400'"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
          <p :class="isWarningSuppressed ? 'text-slate-400' : 'text-orange-400'">
            {{
              isWarningSuppressed
                ? "Broken link detected, warning is being suppressed"
                : "Broken link detected, please manually verify link"
            }}
          </p>
        </div>

        <div
          v-if="isKnownGood && isWarningSuppressed"
          title="Link is not broken, however warnings are suppressed. It is not recommended to suppress known good links."
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5 m-1 inline float-left align-middle text-slate-400"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <p class="text-slate-400">
            Warnings for this link are being suppressed, however link has been verified by
            checker. It is recommended to now disable warning suppression for this link.
          </p>
        </div>
      </div>
      <h2 class="font-bold mt-4" v-if="selectedSection">
        {{ selectedSection?.subheading }}
      </h2>
      <BaseInput label="Label" v-model="linkLabel" :error="errorsLink.linkLabel" />
      <BaseInput label="Address" v-model="linkUrl" :error="errorsLink.linkUrl" />
      <BaseCheckbox label="Suppress Warning" v-model="isWarningSuppressed" class="mt-2" />
      <div
        class="text-blue-600 dark:text-blue-500 hover:underline cursor-pointer mt-4"
        @click="searchSimilarLinks(linkUrl)"
      >
        Show all other portals that have this link
      </div>
    </EditPopup>

    <EditPopup
      entity-name="Link Section"
      :is-visible="isSectionPopupVisible"
      :is-edit-mode="isEditing"
      :save-error="saveError"
      @close="isSectionPopupVisible = false"
      @save="saveSection"
      :delete-enabled="true"
      @delete="deleteSection"
    >
      <BaseInput
        label="Sub Heading"
        v-model="sectionSubheading"
        :error="errorsSection.sectionSubheading"
      />
    </EditPopup>
    <EditPopup
      entity-name="Links from Another Portal"
      :is-visible="isImportPopupVisible"
      :is-edit-mode="false"
      :save-error="saveError"
      :delete-enabled="false"
      save-button-text="Import"
      @close="isImportPopupVisible = false"
      @save="confirmImport"
    >
      <BaseSelect
        v-model="selectedPortal"
        label="Portal"
        :options="portalOptions"
        :error="errorsImport.selectedPortal"
        :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
      />
    </EditPopup>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";

import {
  ImportLinksFromPortalRequest,
  LinkDTO,
  LinkSectionDTO,
  SaveLink,
  SaveLinkSection,
} from "../../store/generatedFetchClient";
import { NameValue } from "../../sharedTypes";

const bldgStore = useBldgStore();
const router = useRouter();

let isLoading = ref(false);
var property = computed(() => bldgStore.property);
var linkSections = ref(null as LinkSectionDTO[]);
const searchText = ref("");

//#region Load Data
onMounted(loadData);
watch(property, loadData);
async function loadData() {
  isLoading.value = true;

  if (property?.value?.portalId) {
    await bldgStore.getLinks(property.value.portalId);

    //Assign fresh sortOrder values based off the current order (in case of unmigrated)
    let sectionSortValue = 0;
    linkSections.value = bldgStore.linkSections.map((linkSection) => {
      let linkSortValue = 0;
      return {
        ...linkSection,
        linkSectionSortOrder: sectionSortValue++,
        links: linkSection.links.map((link) => {
          return { ...link, linkSortOrder: linkSortValue++ };
        }),
      };
    });
  }
  isLoading.value = false;
}
//#endregion load data

//#region Edit LinkSection events

// Popup stuff
let isEditing = ref(false);
let saveError = ref("");
let selectedSection = ref<LinkSectionDTO | undefined>();
let selectedLink = ref<number>(0);

const { handleSubmit: handleSubmitLink, errors: errorsLink } = useForm({
  validationSchema: yup.object({
    linkLabel: yup.string().required().max(50).label("Label"),
    linkUrl: yup.string().required().max(512).label("Address"),
  }),
  initialValues: {
    linkLabel: "",
    linkUrl: "",
  },
});

let isLinkPopupVisible = ref(false);
const { value: linkLabel } = useField<string>("linkLabel");
const { value: linkUrl } = useField<string>("linkUrl");
const { value: isWarningSuppressed } = useField<boolean>("isWarningSuppressed");
const isKnownGood = ref(true);

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

// Link Section Popup stuff
const { handleSubmit: handleSubmitSection, errors: errorsSection } = useForm({
  validationSchema: yup.object({
    sectionSubheading: yup.string().notRequired().max(50).label("Sub Heading"),
  }),
  initialValues: {
    sectionSubheading: "",
  },
});

let isSectionPopupVisible = ref(false);
const { value: sectionSubheading } = useField<string>("sectionSubheading");
let newItemOrderValue = ref(-1);

let saveSection = handleSubmitSection(onValidSubmitSection, onInvalidSubmit);

async function onValidSubmitSection() {
  saveError.value = "";
  var section = {
    id: selectedSection.value?.id,
    portalId: property.value?.portalId,
    subheading: sectionSubheading.value,
    sortOrder: newItemOrderValue.value,
  } as SaveLinkSection;

  saveError.value = await bldgStore.saveLinkSection(section);
  if (!saveError.value) {
    isSectionPopupVisible.value = false;
  }

  loadData();
}

function addSection(top: boolean) {
  if (top) {
    newItemOrderValue.value =
      linkSections?.value?.length > 0
        ? Math.max(...linkSections.value.map((l) => l.linkSectionSortOrder)) - 1
        : -1;
  } else {
    newItemOrderValue.value =
      linkSections?.value?.length > 0
        ? Math.max(...linkSections.value.map((l) => l.linkSectionSortOrder)) + 1
        : -1;
  }
  sectionSubheading.value = "";
  saveError.value = "";
  selectedSection.value = undefined;
  isEditing.value = false;
  isSectionPopupVisible.value = true;
}

function editSection(section: LinkSectionDTO) {
  sectionSubheading.value = section.subheading ?? "";
  saveError.value = "";
  selectedSection.value = section;
  newItemOrderValue.value = section.linkSectionSortOrder;

  isEditing.value = true;
  isSectionPopupVisible.value = true;
}

async function deleteSection() {
  if (selectedSection.value?.id) {
    console.log(`Deleting section ${selectedSection.value.id}`);
    saveError.value = await bldgStore.deleteLinkSection(selectedSection.value.id);
    if (saveError.value) return;
  }
  isSectionPopupVisible.value = false;

  loadData();
}
//#endregion Edit LinkSection events

//#region Edit Link Events

let saveLink = handleSubmitLink(onValidSubmitLink, onInvalidSubmit);

async function onValidSubmitLink() {
  console.log("saving Link");
  saveError.value = "";

  var link = {
    linkId: selectedLink.value ?? 0,
    portalId: property.value?.portalId,
    linkSectionId: selectedSection.value?.id,
    label: linkLabel.value,
    linkUrl: linkUrl.value,
    isWarningSuppressed: isWarningSuppressed.value,
    sortOrder: newItemOrderValue.value,
  } as SaveLink;

  let possibleError = await bldgStore.saveLink(link);
  if (possibleError) {
    saveError.value = possibleError;
    return;
  }
  isLinkPopupVisible.value = false;

  loadData();
}

function addLink(section: LinkSectionDTO | undefined) {
  linkLabel.value = "";
  linkUrl.value = "";
  isWarningSuppressed.value = false;
  isKnownGood.value = true;
  selectedSection.value = section;
  selectedLink.value = 0;
  newItemOrderValue.value =
    section.links?.length > 0
      ? Math.max(...section.links.map((l) => l.linkSortOrder)) - 1
      : -1;
  saveError.value = "";
  isEditing.value = false;
  isLinkPopupVisible.value = true;
}

function editLink(link: LinkDTO, section: LinkSectionDTO) {
  linkLabel.value = link.label ?? "";
  linkUrl.value = link.linkUrl ?? "";
  isWarningSuppressed.value = link.isWarningSuppressed;
  isKnownGood.value = link.isKnownGood;
  selectedLink.value = link.linkId ?? 0;
  selectedSection.value = section;
  newItemOrderValue.value = section.links.find(
    (link) => link.linkId == selectedLink.value
  ).linkSortOrder;

  saveError.value = "";
  isEditing.value = true;
  isLinkPopupVisible.value = true;
}

async function deleteLink() {
  if (selectedLink.value) {
    console.log(`Deleting Link ${selectedLink.value}`);
    saveError.value = await bldgStore.deleteLink(selectedLink.value);
    if (saveError.value) return;
  }
  isLinkPopupVisible.value = false;

  loadData();
}
//#endregion Edit Link Events

//#region Link Sorting
var linkOrderChanged = ref(false);

function swapLinkSort(linkSection: LinkSectionDTO, sortID: number, sortUp: boolean) {
  const selectedLinkItem = linkSection.links.find(
    (link) => link?.linkSortOrder == sortID
  );
  let selectedLinkID = selectedLinkItem?.linkId;

  if (sortUp) {
    const swapLinkItem = linkSection.links.find(
      (link) => link?.linkSortOrder == sortID - 1
    );
    const swapLinkID = swapLinkItem?.linkId;

    const sel = linkSection.links.find((link) => link.linkId == selectedLinkID);
    if (sel) sel.linkSortOrder = sortID - 1;
    const swp = linkSection.links.find((link) => link.linkId == swapLinkID);
    if (swp) swp.linkSortOrder = sortID;

    linkOrderChanged.value = true;
  } else {
    const swapLinkItem = linkSection.links.find(
      (link) => link?.linkSortOrder == sortID + 1
    );
    const swapLinkID = swapLinkItem?.linkId;

    const sel = linkSection.links.find((link) => link?.linkId == selectedLinkID);
    if (sel) sel.linkSortOrder = sortID + 1;
    const swp = linkSection.links.find((link) => link?.linkId == swapLinkID);
    if (swp) swp.linkSortOrder = sortID;

    linkOrderChanged.value = true;
  }

  sortAllLinks();
}

function swapLinkSectionSort(sortID: number, sortUp: boolean) {
  let selectedLinkID = linkSections.value.find(
    (linkSection) => linkSection.linkSectionSortOrder == sortID
  ).id;

  if (sortUp) {
    let swapLinkID = linkSections.value.find(
      (linkSection) => linkSection.linkSectionSortOrder == sortID - 1
    ).id;
    linkSections.value.find(
      (linkSection) => linkSection.id == selectedLinkID
    ).linkSectionSortOrder = sortID - 1;
    linkSections.value.find(
      (linkSection) => linkSection.id == swapLinkID
    ).linkSectionSortOrder = sortID;

    linkOrderChanged.value = true;
  } else {
    let swapLinkID = linkSections.value.find(
      (linkSection) => linkSection.linkSectionSortOrder == sortID + 1
    ).id;
    linkSections.value.find(
      (linkSection) => linkSection.id == selectedLinkID
    ).linkSectionSortOrder = sortID + 1;
    linkSections.value.find(
      (linkSection) => linkSection.id == swapLinkID
    ).linkSectionSortOrder = sortID;

    linkOrderChanged.value = true;
  }

  sortAllLinks();
}

function sortAllLinks() {
  linkSections.value.sort((x, y) => {
    if (x.linkSectionSortOrder < y.linkSectionSortOrder) {
      return -1;
    } else if (x.linkSectionSortOrder > y.linkSectionSortOrder) {
      return 1;
    }
    return 0;
  });

  linkSections.value.forEach((ls) => {
    ls.links.sort((x, y) => {
      if (x.linkSortOrder < y.linkSortOrder) {
        return -1;
      } else if (x.linkSortOrder > y.linkSortOrder) {
        return 1;
      }
      return 0;
    });
  });
}

async function saveSortedLinks() {
  isLoading.value = true;

  let orderToSave = {
    linkSectionsOrders: linkSections.value.map((ls) => {
      return {
        linkSectionID: ls.id,
        linkSectionSortID: ls.linkSectionSortOrder,
        orderedLinks: ls.links.map((link) => {
          return { linkID: link.linkId, linkSortID: link.linkSortOrder };
        }),
      };
    }),
  };

  saveError.value = await bldgStore.saveLinkOrder(orderToSave);

  if (saveError.value == null) linkOrderChanged.value = false;

  loadData();
  isLoading.value = false;
}
//#endregion Link Sorting

//#region Import Links
const { handleSubmit: handleSubmitImport, errors: errorsImport } = useForm({
  validationSchema: yup.object({
    selectedPortal: yup
      .number()
      .required()
      .min(1, "Portal is a required field.")
      .label("Portal"),
  }),
  initialValues: {
    selectedPortal: 0,
  },
});

let isImportPopupVisible = ref(false);
const { value: selectedPortal } = useField<number>("selectedPortal");
let portalOptions = computed(() =>
  bldgStore.properties
    .filter((p) => p.portalId !== property.value.portalId)
    .map((p) => ({ name: p.name, value: p.portalId } as NameValue))
);

let confirmImport = handleSubmitImport(onValidSubmitImport, onInvalidSubmit);

async function onValidSubmitImport() {
  let sortOrder =
    linkSections?.value?.length > 0
      ? Math.max(...linkSections.value.map((l) => l.linkSectionSortOrder)) + 1
      : -1;
  let request = {
    sourcePortalId: selectedPortal.value,
    destinationPortalId: property.value.portalId,
    sortOrder: sortOrder,
  } as ImportLinksFromPortalRequest;

  saveError.value = await bldgStore.importLinksFromPortal(request);
  if (!saveError.value) {
    isImportPopupVisible.value = false;
    loadData();
  }
}

let importLinks = function () {
  selectedPortal.value = 0;

  saveError.value = "";
  isImportPopupVisible.value = true;
};
//#endregion Import Links

function searchLinks() {
  router.push("/staff/linksearch?search=" + searchText.value);
}

function searchSimilarLinks(link: string) {
  router.push("/staff/linksearch?search=" + link);
}
</script>
